import { getPatientsAnswers, getPatientsStat } from "@/api/group";
import { Loader, Modal } from "@/components";
import CustomizedHook from "@/components/project/autocomplatePro";
import Card from "@/pages/admin/dashboard/components/Card";
import { Grid, Typography, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import MembersImg from "@/assets/image/members.png";

function PatientsStat({ patientsStat, allTripsInfo }) {
  const [tripFilterOptions, setTripFilterOptions] = useState([]);
  const [areaFilterOptions, setAreaFilterOptions] = useState([]);
  const [moduleFilterOptions, setModuleFilterOptions] = useState([]);

  const [selectedTrip, setSelectedTrip] = useState(undefined);
  const [selectedArea, setSelectedArea] = useState(undefined);
  const [selectedModule, setSelectedModule] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [servicesStat, setServicesStat] = useState();
  const md = useMediaQuery("(max-width:450px)");

  const [filteredTripsStats, setFilteredTripsStats] = useState([]);

  useEffect(() => {
    setFilteredTripsStats(patientsStat);
  }, [patientsStat]);

  const fetchPatientsAnswers = useCallback(() => {
    getPatientsAnswers(
      moduleFilterOptions.find((e) => e.value === selectedModule)?.key,
    ).then((res) => {
      setServicesStat(res);
    });
  }, [moduleFilterOptions, selectedModule]);

  useEffect(() => {
    if (selectedModule === undefined) return;
    fetchPatientsAnswers();
  }, [selectedModule]);

  useEffect(() => {
    if (!allTripsInfo?.length) {
      setTripFilterOptions([]);
      setAreaFilterOptions([]);
      setModuleFilterOptions([]);
      return;
    }

    const trips = [
      ...new Set(
        allTripsInfo
          .map((item) => item.tripName + "_" + item.tripId)
          .filter(Boolean),
      ),
    ];

    setTripFilterOptions([
      ...trips.map((e) => {
        const tmp = e.split("_");
        return { key: tmp[1], value: tmp[0] };
      }),
      { key: "همه", value: "همه" },
    ]);

    const areas = [
      ...new Set(
        allTripsInfo
          .filter((item) => !selectedTrip || item.tripName === selectedTrip)
          .map((item) => item.name + "_" + item.id)
          .filter(Boolean),
      ),
    ];

    setAreaFilterOptions([
      ...areas.map((e) => {
        const tmp = e.split("_");
        return { key: tmp[1], value: tmp[0] };
      }),
      { key: "همه", value: "همه" },
    ]);

    const modules = [
      ...new Set(
        allTripsInfo
          .filter(
            (item) =>
              (!selectedTrip || item.tripName === selectedTrip) &&
              (!selectedArea || item.name === selectedArea),
          )
          .flatMap(
            (item) =>
              item.moduleStats?.map(
                (module) => module.name + "_" + module.id,
              ) ?? [],
          )
          .filter(Boolean),
      ),
    ];

    setModuleFilterOptions([
      ...modules.map((e) => {
        const tmp = e.split("_");
        return { key: tmp[1], value: tmp[0] };
      }),
      { key: "همه", value: "همه" },
    ]);

    if (selectedArea && !areas.includes(selectedArea)) {
      setSelectedArea("");
      setSelectedModule("");
      return;
    }

    if (selectedModule && !modules.includes(selectedModule)) {
      setSelectedModule("");
    }
  }, [allTripsInfo, selectedTrip, selectedArea]);

  const fetchPatientsStat = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();

    if (selectedArea !== undefined && selectedArea !== "") {
      params.append(
        "areaId",
        areaFilterOptions.find((e) => e.value === selectedArea)?.key,
      );
    }
    if (selectedTrip !== undefined && selectedTrip !== "") {
      params.append(
        "tripId",
        tripFilterOptions.find((e) => e.value === selectedTrip)?.key,
      );
    }

    if (selectedModule !== undefined && selectedModule !== "") {
      params.append(
        "moduleId",
        moduleFilterOptions.find((e) => e.value === selectedModule)?.key,
      );
    }
    getPatientsStat(params.toString())
      .then((res) => {
        setFilteredTripsStats(res);
      })
      .finally(() => setLoading(false));
  }, [
    selectedModule,
    selectedArea,
    selectedTrip,
    tripFilterOptions,
    areaFilterOptions,
    moduleFilterOptions,
  ]);

  useEffect(() => {
    if (selectedModule === "" && selectedArea === "" && selectedTrip === "") {
      setFilteredTripsStats(patientsStat);
      return;
    }
    fetchPatientsStat();
  }, [selectedModule, selectedArea, selectedTrip]);

  return (
    <>
      {loading && <Loader />}
      {!loading && (
        <Grid
          display={"flex"}
          flexDirection={"column"}
          gap={"10px"}
          width={"100%"}
        >
          <Grid
            display="flex"
            flexDirection="row"
            gap="10px"
            flexWrap="wrap"
            width={"100%"}
          >
            <CustomizedHook
              parentWidth="170px"
              width={"170px"}
              placeholder={"اردو"}
              label="اردو"
              options={tripFilterOptions}
              onChange={(_, newValue) =>
                setSelectedTrip(
                  newValue.value === "همه" ? undefined : newValue.value,
                )
              }
              value={
                selectedTrip === undefined
                  ? "همه"
                  : tripFilterOptions.find((e) => e.value === selectedTrip) ||
                    null
              }
            />
            <CustomizedHook
              parentWidth="170px"
              width={"170px"}
              placeholder={"منطقه"}
              label="منطقه"
              options={areaFilterOptions}
              onChange={(_, newValue) =>
                setSelectedArea(
                  newValue.value === "همه" ? undefined : newValue.value,
                )
              }
              value={selectedArea === undefined ? "همه" : selectedArea || null}
            />
            <CustomizedHook
              parentWidth="170px"
              width={"170px"}
              placeholder={"بخش"}
              label="بخش"
              options={moduleFilterOptions}
              onChange={(_, newValue) =>
                setSelectedModule(
                  newValue.value === "همه" ? undefined : newValue.value,
                )
              }
              value={
                selectedModule === undefined ? "همه" : selectedModule || null
              }
            />
          </Grid>
          <Grid
            display={"flex"}
            flexDirection={"row"}
            gap={"10px"}
            width={"100%"}
            flexWrap={"wrap"}
          >
            <Modal width={md ? "100%" : "calc(100% - 560px)"}>
              <Grid
                display={"flex"}
                flexDirection={"column"}
                gap={"15px"}
                justifyContent={"space-between"}
                height={"100%"}
              >
                <Grid display={"flex"} flexDirection={"column"}>
                  <img
                    height={"100px"}
                    width={"fit-content"}
                    src={MembersImg}
                    style={{ margin: "15px auto" }}
                  />
                  <Typography variant="h6" sx={{ color: "var(--emr-text)" }}>
                    لیست مراجعین
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "var(--emr-primary-color-text)",
                      marginTop: "10px",
                    }}
                  >
                    شما میتوانید لیست کل بیماران گروه خود را مشاهده و مدیریت
                    کنید.
                  </Typography>
                </Grid>
                <Typography
                  sx={{ color: "var(--emr-primary)", cursor: "pointer" }}
                >
                  مشاهده
                </Typography>
              </Grid>
            </Modal>

            <Grid
              display="flex"
              flexDirection="row"
              gap="20px"
              width={"540px"}
              flexWrap={"wrap"}
            >
              <Card
                title={"کل مراجعین"}
                width={md ? "250px" : "250px"}
                sx={{ height: "100px" }}
                counter={filteredTripsStats.total}
                unit={"نفر"}
              />
              <Card
                title={"کل مراجعین کودک"}
                width={md ? "250px" : "250px"}
                sx={{ height: "100px" }}
                counter={filteredTripsStats.child}
                unit={"نفر"}
              />
              <Card
                title={"کل مراجعین مرد"}
                width={md ? "250px" : "250px"}
                sx={{ height: "100px" }}
                counter={filteredTripsStats.male}
                unit={"نفر"}
              />
              <Card
                title={"کل مراجعین زن"}
                width={md ? "250px" : "250px"}
                sx={{ height: "100px" }}
                counter={filteredTripsStats.female}
                unit={"نفر"}
              />
            </Grid>
          </Grid>

          {servicesStat && (
            <Grid
              display={"flex"}
              flexDirection={"row"}
              flexWrap={"wrap"}
              gap={"15px"}
              rowGap={"20px"}
            >
              {servicesStat.map((service) => (
                <>
                  {service.optionsDigest.map((option, index) => (
                    <Card
                      key={index}
                      title={service.text}
                      counter={option.counter}
                      additionalChild={
                        <Typography color="primary" variant="body1">
                          {option.option.value}
                        </Typography>
                      }
                    />
                  ))}
                </>
              ))}
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
}

export default PatientsStat;
